import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Link as MuiLink,
  Paper,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { Close as CloseIcon, Cookie as CookieIcon } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { COMPANY_INFO } from '../../config/company';

const STORAGE_KEY = 'cookie_consent.v1';

export interface CookiePrefs {
  necessary: true; // always true
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
  decidedAt: string; // ISO date
}

function loadPrefs(): CookiePrefs | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed !== 'object' || !parsed.decidedAt) return null;
    return { ...parsed, necessary: true };
  } catch {
    return null;
  }
}

function savePrefs(prefs: CookiePrefs) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  window.dispatchEvent(new CustomEvent('cookie-consent-changed', { detail: prefs }));
}

/**
 * POPIA-compliant cookie consent banner.
 *
 * Shown on first visit and until the user makes an explicit choice. Once
 * a choice is stored in localStorage under `cookie_consent.v1`, the
 * banner is hidden. User can re-open the settings modal any time via the
 * `open-cookie-settings` window event (fired from the Cookie Policy page).
 */
export function CookieConsentBanner() {
  const [shown, setShown] = useState<boolean>(() => loadPrefs() === null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [functional, setFunctional] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  // Open settings modal when any part of the app asks us to.
  useEffect(() => {
    const openHandler = () => {
      const existing = loadPrefs();
      setFunctional(!!existing?.functional);
      setAnalytics(!!existing?.analytics);
      setMarketing(!!existing?.marketing);
      setSettingsOpen(true);
    };
    window.addEventListener('open-cookie-settings', openHandler);
    return () => window.removeEventListener('open-cookie-settings', openHandler);
  }, []);

  const commit = (prefs: Omit<CookiePrefs, 'decidedAt' | 'necessary'>) => {
    savePrefs({ necessary: true, ...prefs, decidedAt: new Date().toISOString() });
    setShown(false);
    setSettingsOpen(false);
  };

  const acceptAll = () => commit({ functional: true, analytics: true, marketing: true });
  const rejectAll = () => commit({ functional: false, analytics: false, marketing: false });
  const saveCurrent = () => commit({ functional, analytics, marketing });

  return (
    <>
      <Collapse in={shown} unmountOnExit>
        <Paper
          elevation={6}
          sx={{
            position: 'fixed',
            // Above the mobile bottom bar when signed in (the variable is 0
            // on public pages), and clear of the iPhone home indicator.
            bottom: { xs: 'var(--app-bottom-inset)', md: 16 },
            left: { xs: 0, md: 16 },
            right: { xs: 0, md: 16 },
            zIndex: 'snackbar',
            borderRadius: { xs: '16px 16px 0 0', md: 3 },
            p: { xs: 2, md: 3 },
            pb: { xs: 'calc(16px + env(safe-area-inset-bottom))', md: 3 },
            backgroundColor: '#1A1D1F',
            color: 'white',
          }}
        >
          <Container maxWidth="lg" disableGutters>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 1.5, md: 2 }} alignItems={{ md: 'center' }}>
              <Box sx={{ display: 'flex', gap: 1.5, flex: 1 }}>
                <CookieIcon sx={{ color: 'var(--color-primary-500)', mt: 0.5, display: { xs: 'none', sm: 'block' } }} />
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                    We value your privacy
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    {COMPANY_INFO.legalName} uses strictly necessary cookies to make the Platform work.
                    With your informed consent (POPIA s.11), we'd also like to use functional,
                    analytics, and marketing cookies. You can accept all, reject all, or choose.
                    See our{' '}
                    <MuiLink component={Link} to="/legal/cookies" sx={{ color: 'var(--color-primary-500)' }}>
                      Cookie Policy
                    </MuiLink>.
                  </Typography>
                </Box>
              </Box>
              {/* One row of three on phones too: stacked, the buttons alone
                  took ~130px and the banner hid the whole sign-in form. */}
              <Stack direction="row" spacing={1} sx={{ flexShrink: 0, '& > .MuiButton-root': { flex: { xs: 1, md: 'none' }, px: { xs: 1, md: 2.5 } } }}>
                <Button onClick={rejectAll} variant="outlined" sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.4)' }}>
                  Reject all
                </Button>
                <Button onClick={() => setSettingsOpen(true)} variant="outlined" sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.4)' }}>
                  Customise
                </Button>
                <Button onClick={acceptAll} variant="contained" sx={{ backgroundColor: 'var(--color-primary-500)', color: 'var(--ink)', '&:hover': { backgroundColor: '#244BC0' } }}>
                  Accept all
                </Button>
              </Stack>
            </Stack>
          </Container>
        </Paper>
      </Collapse>

      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Cookie preferences
          <IconButton onClick={() => setSettingsOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            You can choose which categories of cookies to allow. Strictly necessary cookies are
            always on because the Platform cannot function without them.
          </Typography>

          <CategoryRow
            title="Strictly necessary"
            description="Authentication, session management, security. Cannot be disabled."
            checked
            disabled
            onChange={() => {}}
          />
          <CategoryRow
            title="Functional"
            description="Remember your preferences: theme, language, layout."
            checked={functional}
            onChange={setFunctional}
          />
          <CategoryRow
            title="Analytics"
            description="Help us understand how features are used, in aggregate, so we can improve the Platform."
            checked={analytics}
            onChange={setAnalytics}
          />
          <CategoryRow
            title="Marketing"
            description="Measure the effectiveness of campaigns on our public marketing site. Never inside the Platform."
            checked={marketing}
            onChange={setMarketing}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
          <Button onClick={rejectAll}>Reject all</Button>
          <Stack direction="row" spacing={1}>
            <Button onClick={acceptAll}>Accept all</Button>
            <Button variant="contained" onClick={saveCurrent}>Save choices</Button>
          </Stack>
        </DialogActions>
      </Dialog>
    </>
  );
}

function CategoryRow({ title, description, checked, onChange, disabled }: {
  title: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <Box sx={{
      py: 1.5, borderTop: '1px solid var(--hairline)', display: 'flex', gap: 2, alignItems: 'center',
      '&:last-of-type': { borderBottom: '1px solid var(--hairline)' },
    }}>
      <Box sx={{ flex: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{title}</Typography>
        <Typography variant="caption" color="text.secondary">{description}</Typography>
      </Box>
      <FormControlLabel
        control={<Switch checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />}
        label=""
        sx={{ mr: 0 }}
      />
    </Box>
  );
}

/**
 * Utility for app code that wants to check whether a category is enabled
 * before loading an optional script (analytics, marketing).
 */
export function hasConsent(category: keyof Omit<CookiePrefs, 'decidedAt' | 'necessary'>): boolean {
  const p = loadPrefs();
  return !!p && !!p[category];
}

export default CookieConsentBanner;
